import type { AssessmentAnswerDto, EnglishTestQuestionDto } from '@/api/generated/models'
import { ENGLISH_TEST_MESSAGES } from '../../../constants/enrollmentContent'
import { byDisplayOrder } from '../questionUtils'
import type { EnglishTestAnswerReview } from '../types'
import { buildAnswerReview, getCorrectAnswerTexts, getSortedCorrectAnswers, normalizeAnswerText } from './shared'

type AssessmentAnswerResponse = {
  answers?: AssessmentAnswerDto[]
}

export function buildMultipleChoiceSubmittedAnswer(
  question: EnglishTestQuestionDto,
  optionIndex: number,
  response: AssessmentAnswerResponse
) {
  const sortedAnswers = [...(question.answers ?? [])].sort(byDisplayOrder)
  const answer = sortedAnswers[optionIndex]

  if (!question.id || !answer?.id) {
    throw new Error(ENGLISH_TEST_MESSAGES.missingAnswerData)
  }

  const savedAnswer = response.answers?.find(item => item.questionId === question.id)
  const correctAnswers = sortedAnswers.filter(item => item.correct === true)

  return buildAnswerReview(question, {
    selectedAnswerId: answer.id ?? null,
    selectedAnswerText: answer.answerText ?? '',
    correctAnswerTexts: correctAnswers.map(item => item.answerText ?? '').filter(Boolean),
    locallyCorrect: answer.correct ?? null,
    backendCorrect: savedAnswer?.correct ?? null,
    pointsEarned: savedAnswer?.pointsEarned ?? null
  })
}

export function buildTextSubmittedAnswer(
  question: EnglishTestQuestionDto,
  textAnswer: string,
  response: AssessmentAnswerResponse
) {
  const correctAnswers = getSortedCorrectAnswers(question)
  const normalizedTextAnswer = normalizeAnswerText(textAnswer)
  const locallyCorrect =
    correctAnswers.length > 0
      ? correctAnswers.some(item => normalizeAnswerText(item.answerText) === normalizedTextAnswer)
      : null
  const savedAnswer = response.answers?.find(item => item.questionId === question.id)

  return buildAnswerReview(question, {
    selectedAnswerId: null,
    selectedAnswerText: textAnswer,
    correctAnswerTexts: getCorrectAnswerTexts(question),
    locallyCorrect,
    backendCorrect: savedAnswer?.correct ?? null,
    pointsEarned: savedAnswer?.pointsEarned ?? null
  })
}

export function buildCompletedAssessmentReviewAnswers(
  questions: EnglishTestQuestionDto[],
  submittedAnswers: Record<number, EnglishTestAnswerReview>,
  completedAnswers: AssessmentAnswerDto[] | undefined
) {
  const backendAnswersByQuestion = new Map(
    (completedAnswers ?? [])
      .filter(answer => typeof answer.questionId === 'number')
      .map(answer => [answer.questionId!, answer])
  )

  return questions.map(question => {
    const existing = question.id ? submittedAnswers[question.id] : undefined
    const backendAnswer = question.id ? backendAnswersByQuestion.get(question.id) : undefined

    return buildAnswerReview(question, {
      selectedAnswerId: existing?.selectedAnswerId ?? backendAnswer?.selectedAnswerId ?? null,
      selectedAnswerText:
        existing?.selectedAnswerText ??
        backendAnswer?.selectedAnswerText ??
        backendAnswer?.textAnswer ??
        '',
      correctAnswerTexts: existing?.correctAnswerTexts ?? getCorrectAnswerTexts(question),
      locallyCorrect: existing?.locallyCorrect ?? null,
      backendCorrect: backendAnswer?.correct ?? existing?.backendCorrect ?? null,
      pointsEarned: backendAnswer?.pointsEarned ?? existing?.pointsEarned ?? null
    })
  })
}
